import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {list} from "../core/Reflection";
import {Scalable} from "../core/Scalable";
import {Poly} from "../core/Shape";
import {Signal} from "../core/Signal";
import {Sprite} from "../core/Sprite";
import {Button} from "./Button";

export class Carousel<T extends PIXI.Container & { enabled: boolean } & typeof Scalable> extends Container {

    public readonly bg = this.addChild(new Sprite());
    public readonly itemsContainer = this.addChild(new Container());
    public readonly itemsRect = {x: 0, y: 0, width: 0, height: 0};
    public readonly itemsMask = list(() => ({x: 0, y: 0}));
    public readonly buttonNext = this.addChild(new Button());
    public readonly buttonPrev = this.addChild(new Button());

    public readonly onScrollComplete = Signal();
    public readonly visibleItems: number = 1;
    public readonly itemsToScroll: number = 1;
    public verticalAnchor: number = 0.5;

    private _isScrolling = false;
    private _firstItemIndex = 0;

    constructor(visibleItems: number, itemsToScroll: number) {
        super();

        this.visibleItems = visibleItems;
        this.itemsToScroll = itemsToScroll;

        this.buttonNext.visible = false;
        this.buttonPrev.visible = false;

        this.buttonNext.onClick.add(() => {
            this.scrollItems(1);
        });
        this.buttonPrev.onClick.add(() => {
            this.scrollItems(-1);
        });
    }

    public onLayout() {
        this.itemsContainer.mask = this.addChild(new Poly(this.itemsMask.reduce((a, t) => {
            a.push(t.x, t.y);
            return a;
        }, [] as number[]), 0));
    }

    public setItems(items: T[], firstItemIndex = 0) {

        this.itemsContainer.killTweens();
        this.itemsContainer.destroyChildren();
        this.itemsContainer.x = this.itemsRect.x;
        this.itemsContainer.y = this.itemsRect.y;
        this._firstItemIndex = 0;
        this._isScrolling = false;

        const gap = (this.itemsRect.width - this.itemsRect.height * this.visibleItems) / (this.visibleItems - 1);
        let x = 0;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            this.itemsContainer.addChild(item);

            item.setBounds(this.itemsRect.height + gap, this.itemsRect.height);
            item.scaleXY = Math.min(item.scaleXY, 2);

            item.y = this.itemsRect.height * this.verticalAnchor;
            item.x = x + this.itemsRect.height * 0.5;
            x += this.itemsRect.height + gap;
        }

        if (firstItemIndex > 0) {
            let index = firstItemIndex;
            const maxIndex = this.itemsContainer.children.length - this.visibleItems;
            if (index > maxIndex) {
                index = maxIndex;
            }
            this._firstItemIndex = index;
            const p = this.itemsRect.height * 0.5;
            this.itemsContainer.x = this.itemsRect.x - this.itemsContainer.getChildAt(index).x + p;
        }

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const b = i >= this._firstItemIndex && i < this._firstItemIndex + this.visibleItems;
            item.enabled = b;
            item.alpha = b ? 1 : 0;
        }

        if (items.length < this.visibleItems) {
            const n = this.visibleItems - items.length;
            const padding = this.itemsRect.height * n / 2 + gap * n / 2;
            for (const item of items) {
                item.x += padding;
            }
        }
        this.buttonNext.visible = this.buttonPrev.visible = items.length > this.visibleItems;
    }

    public getItems(): T[] {
        return this.itemsContainer.getChildren<T>();
    }

    public getVisibleItems(): T[] {
        return this.itemsContainer.getChildren<T>().slice(this._firstItemIndex, this._firstItemIndex + this.visibleItems);
    }

    public getItem(index: number, visible: boolean = false): T {
        if (visible) {
            index += this._firstItemIndex;
        }
        while (index < 0) {
            index += this.itemsContainer.children.length;
        }
        index = index % this.itemsContainer.children.length;
        return this.itemsContainer.getChildAt(index);
    }

    private scrollItems(direction: number) {
        if (this._isScrolling) {
            return;
        }
        if (this.itemsContainer.children.length <= this.visibleItems) {
            return;
        }

        let index = this._firstItemIndex + direction * this.itemsToScroll;
        const maxIndex = this.itemsContainer.children.length - this.visibleItems;
        if (index < 0) {
            index = 0;
        }
        if (index > maxIndex) {
            index = maxIndex;
        }

        if (this._firstItemIndex !== index) {
            this._isScrolling = true;

            const time = Math.abs(this._firstItemIndex - index) * 0.2;
            this._firstItemIndex = index;

            const min = 0;
            const max = this.itemsRect.width;
            const p = this.itemsRect.height * 0.5;
            const items = this.itemsContainer.getChildren<Button>();

            this.itemsContainer.moveXTo(this.itemsRect.x - this.itemsContainer.getChildAt(index).x + p, time,
                Ease.backOut.config((max + p) / max), {
                    onUpdate: () => {
                        for (let i = 0; i < items.length; i++) {
                            const item = items[i];
                            const x = item.x - (this.itemsRect.x - this.itemsContainer.x);
                            if (x <= min || x >= max) {
                                item.alpha = 0;
                                item.enabled = false;
                            } else if (x >= min + p && x <= max - p) {
                                item.alpha = 1;
                                item.enabled = true;
                            } else if (x > min && x < min + p) {
                                if (direction > 0 && i === index) {
                                    item.alpha = 1;
                                    item.enabled = true;
                                } else {
                                    item.alpha = (x - min) / p;
                                    item.enabled = item.alpha > 0.5;
                                }
                            } else {
                                if (direction < 0 && i === index + this.visibleItems - 1) {
                                    item.alpha = 1;
                                    item.enabled = true;
                                } else {
                                    item.alpha = (max - x) / p;
                                    item.enabled = item.alpha > 0.5;
                                }
                            }
                        }
                    },
                    onComplete: () => {
                        this._isScrolling = false;
                        this.onScrollComplete();
                    },
                });
        }
    }
}
